import { Affix, Button, Col, Row, Space, Typography } from "antd";
import Img from "assets/images";
import CommonCard from "components/CommonCard";
import CreateButton from "components/CreateButton";
import CustomModal from "components/CustomModal";
import { CAMPAIGN_TYPE, COMBO_CAMPAIGN, isProductPromoProgramBudgetRequired } from "features/Campaign/constants";
import { t } from "i18next";
import { groupBy, sumBy } from "lodash";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { formatCurrency, getQuery } from "utils/helperFuncs";
import styles from "./index.module.scss";

const ButtonGroup = ({
  data,
  loadingCheckDuplicateCodes,
  visibleConfirmModal,
  handleOpenConfirmModal,
  handleCancelConfirmModal,
  handleActionCampaign,
  createLoading,
  isEditMode,
}) => {
  const GET_QUERY = getQuery();
  const history = useHistory();

  const groupedData = useMemo(() => groupBy(data?.promotionCampaignTierGroups, "type"), [data]);
  const fixedPriceArray = groupedData[COMBO_CAMPAIGN.FIXED_PRICE] || [];

  const budgetProductQuantity = sumBy(fixedPriceArray, (item) => parseInt(item.budgetProductQuantity));

  const { totalBudgetAmount, totalBudgetQuantity } = useMemo(() => {
    const grouped = groupBy(data?.promotionCampaignTierGroups, "type");
    const discountMoney = grouped[COMBO_CAMPAIGN.DISCOUNT_MONEY] || [];
    const gifts = grouped[COMBO_CAMPAIGN.GIFT] || [];
    const totalAmount = sumBy(discountMoney, (item) => sumBy(item.promotionCampaignTiers, "budgetAmount"));
    const flattenedGifts = gifts.flatMap((item) => item.promotionCampaignTiers?.flatMap((item) => item.conditionGifts));
    const budgetQuantity = sumBy(flattenedGifts, (gift) => sumBy(gift.conditionPromotionProducts, "budgetQuantity"));

    return {
      totalBudgetAmount: totalAmount,
      totalBudgetQuantity: budgetQuantity,
    };
  }, [data]);

  const productPromoLen = () => {
    const num = data?.promotionCampaignTierGroups?.[0]?.promotionCampaignTiers?.length || 0;
    return num < 10 ? `0${num}` : num;
  };

  const totalBudgetAmountProductPromo = data?.promotionCampaignTierGroups?.[0]?.promotionCampaignTiers?.reduce(
    (total, item) => {
      return total + (item.maxBudgetPerSeller ?? item.budgetAmount ?? 0);
    },
    0
  );

  const isCashDiscount =
    GET_QUERY.campaignType === CAMPAIGN_TYPE.CASH_DISCOUNT || GET_QUERY.campaignType === "CASH_DISCOUNT";

  const isProductPromo = GET_QUERY.campaignType === CAMPAIGN_TYPE.PRODUCT_PROMO;
  const isProductPromoProgramBudget = isProductPromoProgramBudgetRequired(data?.sellerSegmentType);
  const showProductPromoBudgetRow = !isProductPromo || !isProductPromoProgramBudget || data?.maxBudgetPerSeller;

  return (
    <Affix offsetBottom={isCashDiscount ? 20 : 10} style={{ marginTop: "8px" }}>
      <div className={`${styles["form-buttons"]} ${isCashDiscount ? styles["cash-discount-form-buttons"] : ""}`}>
        <CommonCard
          className={`${styles["form-buttons-container"]} ${
            isCashDiscount ? styles["cash-discount-form-buttons-container"] : ""
          }`}
        >
          <CustomModal
            message={<Typography.Text>{t("campaign.combo.cancel")}</Typography.Text>}
            customComponent={<Button className={`${styles["secondary-button"]}`}>{t("common.cancel")}</Button>}
            footer={false}
            icon={Img.QuestionIcon}
            onOke={() => history.push("/campaign")}
          />
          <CustomModal
            {...{
              customComponent: (
                <CreateButton
                  loading={loadingCheckDuplicateCodes}
                  title={isEditMode ? t("common.update") : t("common.create")}
                />
              ),
              footer: false,
              centered: isCashDiscount,
              visible: visibleConfirmModal,
              onOpen: handleOpenConfirmModal,
              onOke: handleActionCampaign,
              onCancel: handleCancelConfirmModal,
              buttonLoading: createLoading,
              title: (
                <Typography.Title level={4}>
                  {isEditMode
                    ? t("campaign.combo.confirmUpdateCampaignLabel")
                    : t("campaign.combo.confirmCreateCampaignLabel")}
                </Typography.Title>
              ),
              message: (
                <div style={{ textAlign: "left" }}>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Row
                        gutter={[30, 30]}
                        style={{
                          backgroundColor: "#E7F0FF",
                          padding: "12px 0",
                          borderRadius: "12px",
                        }}
                      >
                        <Col span={12}>
                          <Typography.Text strong>{t("campaign.form.campaignName")}</Typography.Text>
                        </Col>
                        <Col span={12}>
                          <Typography.Text>{data?.name}</Typography.Text>
                        </Col>
                        <Col span={12}>
                          <Typography.Text strong>{t("campaign.form.campaignPeriod")}</Typography.Text>
                        </Col>
                        <Col span={12}>
                          <Space direction="vertical">
                            <Typography.Text>
                              {t("product.inputRange.from")}: {data?.startDate ? data?.startDate : "- -"}
                            </Typography.Text>
                            <Typography.Text>
                              {t("campaign.detailPromo.to")}: {data?.endDate ? data?.endDate : "- -"}
                            </Typography.Text>
                          </Space>
                        </Col>
                        {GET_QUERY.campaignType === CAMPAIGN_TYPE.PRODUCT_PROMO ? (
                          <>
                            <Col span={12}>
                              <Typography.Text strong>{t("campaign.detailPromo.sumProductPromo")}</Typography.Text>
                            </Col>
                            <Col span={12}>{productPromoLen()}</Col>
                          </>
                        ) : null}
                        {!isCashDiscount && showProductPromoBudgetRow && (
                          <>
                            <Col span={12}>
                              <Typography.Text strong>
                                {isProductPromo && isProductPromoProgramBudget
                                  ? t("campaign.detailPromo.programBudgetLabel")
                                  : t("campaign.detailPromo.sumBudget")}
                              </Typography.Text>
                            </Col>
                            <Col span={12}>
                              {GET_QUERY.campaignType === CAMPAIGN_TYPE.COMBO_PROMO ? (
                                <Space direction="vertical">
                                  {totalBudgetAmount ? (
                                    <Typography.Text>
                                      {t("campaign.combo.totalPromotionAmount")}: {formatCurrency(totalBudgetAmount)}
                                    </Typography.Text>
                                  ) : null}
                                  {totalBudgetQuantity !== 0 && (
                                    <Typography.Text>
                                      {t("campaign.detailPromo.totalAmountToGifts")}: {totalBudgetQuantity}
                                    </Typography.Text>
                                  )}
                                  {budgetProductQuantity !== 0 && (
                                    <Typography.Text>
                                      {t("campaign.detailPromo.totalPriceFix")}: {budgetProductQuantity}
                                    </Typography.Text>
                                  )}
                                </Space>
                              ) : GET_QUERY.campaignType === CAMPAIGN_TYPE.ORDER_PROMO ? (
                                <Typography.Text>{formatCurrency(data?.totalBudget)}</Typography.Text>
                              ) : isProductPromo ? (
                                <Typography.Text>
                                  {formatCurrency(
                                    isProductPromoProgramBudget
                                      ? data?.maxBudgetPerSeller
                                      : totalBudgetAmountProductPromo
                                  )}
                                </Typography.Text>
                              ) : (
                                <Typography.Text>{formatCurrency(totalBudgetAmountProductPromo)}</Typography.Text>
                              )}
                            </Col>
                          </>
                        )}
                        {GET_QUERY.campaignType === CAMPAIGN_TYPE.ORDER_PROMO && (
                          <>
                            <Col span={12}>
                              <Typography.Text strong>Số mức ưu đãi</Typography.Text>
                            </Col>
                            <Col span={12}>
                              <Typography.Text>{data?.promoLevelCount ?? 0} mức</Typography.Text>
                            </Col>
                          </>
                        )}
                        {GET_QUERY.campaignType === CAMPAIGN_TYPE.ORDER_PROMO && (
                          <>
                            <Col span={12}>
                              <Typography.Text strong>Số mã vouchers</Typography.Text>
                            </Col>
                            <Col span={12}>
                              <Typography.Text>{data?.promoLevelCount ?? 0} mã</Typography.Text>
                            </Col>
                          </>
                        )}
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Typography.Text>
                        {t(`campaign.combo.${isEditMode ? "editCampaign" : "createCampaign"}`)}
                      </Typography.Text>
                    </Col>
                  </Row>
                </div>
              ),
            }}
          />
        </CommonCard>
      </div>
    </Affix>
  );
};

export default ButtonGroup;
